/**
 * frontend/src/pages/Terms.js
 * 
 * Public terms and conditions page.
 * Defines the legal agreement between ChangeFox and its users regarding service usage.
 */
import React from 'react';
import { Container } from 'react-bootstrap';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import BackgroundShapes from '../components/common/BackgroundShapes';
import Footer from '../components/common/Footer';
import SEO from '../components/common/SEO';
import logo from '../assets/logo.png';
import '../styles/landing.css';

const Terms = () => {
  return (
    <div className="landing-page">
      <SEO
        title="Terms and Conditions - ChangeFox"
        description="Read the terms and conditions for using ChangeFox compliance change management platform."
        keywords="terms and conditions, terms of service, legal, changefox"
        path="/terms"
      />
      <BackgroundShapes />

      {/* Navigation */}
      <motion.nav
        className="landing-nav"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Container>
          <div className="nav-content">
            <Link to="/" className="nav-logo">
              <img src={logo} alt="ChangeFox" className="nav-logo-img" />
            </Link>
            <div className="nav-links">
              <Link to="/" className="nav-link">Home</Link>
              <Link to="/login" className="nav-link">Sign In</Link>
            </div>
          </div>
        </Container>
      </motion.nav>

      {/* Content */}
      <section className="legal-section">
        <Container>
          <motion.div
            className="legal-content"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="legal-title">Terms and Conditions</h1>
            <p className="legal-updated">Last updated: January 2026</p>

            <div className="legal-body">
              <h2>1. Acceptance of Terms</h2>
              <p>
                By accessing and using ChangeFox ("Service"), you accept and agree to be bound by the terms
                and provisions of this agreement. If you do not agree to these terms, please do not use our Service.
              </p>

              <h2>2. Description of Service</h2>
              <p>
                ChangeFox provides a compliance change management platform that allows organizations to submit,
                track, and manage change requests. The Service includes features such as change request submission,
                approval workflows, team collaboration, and audit trails.
              </p>

              <h2>3. User Accounts</h2>
              <p>
                To access certain features of the Service, you must create an account. You are responsible for:
              </p>
              <ul>
                <li>Maintaining the confidentiality of your account credentials</li>
                <li>All activities that occur under your account</li>
                <li>Notifying us immediately of any unauthorized use</li>
              </ul>

              <h2>4. Acceptable Use</h2>
              <p>You agree not to:</p>
              <ul>
                <li>Use the Service for any unlawful purpose</li>
                <li>Attempt to gain unauthorized access to the Service</li>
                <li>Interfere with or disrupt the Service</li>
                <li>Upload malicious code or content</li>
                <li>Violate any applicable laws or regulations</li>
              </ul>

              <h2>5. Intellectual Property</h2>
              <p>
                The Service and its original content, features, and functionality are owned by ChangeFox and
                are protected by international copyright, trademark, and other intellectual property laws.
              </p>

              <h2>6. Data Privacy</h2>
              <p>
                Your use of the Service is also governed by our Privacy Policy. Please review our Privacy Policy
                to understand our practices regarding your personal information.
              </p>

              <h2>7. Limitation of Liability</h2>
              <p>
                ChangeFox shall not be liable for any indirect, incidental, special, consequential, or punitive
                damages resulting from your use or inability to use the Service.
              </p>

              <h2>8. Modifications to Service</h2>
              <p>
                We reserve the right to modify or discontinue the Service at any time without notice. We shall
                not be liable to you or any third party for any modification, suspension, or discontinuance.
              </p>

              <h2>9. Termination</h2>
              <p>
                We may terminate or suspend your account and access to the Service immediately, without prior
                notice, for conduct that we believe violates these Terms or is harmful to other users, us, or
                third parties.
              </p>

              <h2>10. Governing Law</h2>
              <p>
                These Terms shall be governed by and construed in accordance with applicable laws, without
                regard to conflict of law principles.
              </p>

              <h2>11. Contact Information</h2>
              <p>
                If you have any questions about these Terms, please contact us at{' '}
                <a href="mailto:contact@changefox.ai">contact@changefox.ai</a>.
              </p>
            </div>
          </motion.div>
        </Container>
      </section>

      <Footer />
    </div>
  );
};

export default Terms;
